/**
 * MHR Review Types
 * Types for review results and human verification
 */

import type { ConfidenceLevel } from './summary';

// Result types matching backend response
export type ResultType = 'met' | 'not_met' | 'uncertain' | 'not_found' | 'contradictory';
export type ReviewStatus =
  | 'pending'
  | 'budget_pending'
  | 'in_progress'
  | 'pass_1_exclusions'
  | 'pass_2_inclusions'
  | 'pass_3_verification'
  | 'completed'
  | 'failed'
  | 'cancelled';

export interface Citation {
  citation_id: string;
  page_number: number;
  extracted_text: string;
  evidence_type: string;
}

export interface CriterionResult {
  id: string;
  criterion_id: string;
  criterion_number: string;
  criterion_text: string;
  type: 'inclusion' | 'exclusion';
  result: ResultType;
  confidence_level: ConfidenceLevel;
  confidence_score: number;
  evidence: Citation[];
  ai_reasoning: string;
  has_contradiction: boolean;
  requires_manual_review: boolean;
  human_verified: boolean;
  human_result?: ResultType;
  human_notes?: string;
}

export interface ReviewSummary {
  total_criteria: number;
  met: number;
  not_met: number;
  uncertain: number;
  not_found: number;
  contradictory: number;
  exclusions_met: number;
}

export interface DocumentCoverage {
  total_pages: number;
  pages_analyzed: number;
  pages_with_findings: number[];
  quality_score: number;
}

export interface ReviewCost {
  total_cost: number;
  cache_savings: number;
}

export interface Review {
  review_id: string;
  patient_id: string;
  protocol_id: string;
  document_id: string;
  status: ReviewStatus;
  overall_result: string;
  created_at: string;
  completed_at?: string;
  summary: ReviewSummary;
  criteria_results: CriterionResult[];
  document_coverage: DocumentCoverage;
  cost: ReviewCost;
}

export interface ReviewStatusResponse {
  status: ReviewStatus;
  progress_percent: number;
  current_pass: number;
  current_criterion: number;
  total_criteria: number;
  pages_analyzed: number;
  started_at: string;
  completed_at?: string;
}

export interface VerificationPayload {
  human_result: ResultType;
  notes?: string;
  override_reason?: string;
}

export interface ExportResponse {
  url: string;
  filename: string;
}

// Display labels for result types
export const RESULT_TYPE_LABELS: Record<ResultType, string> = {
  met: 'Met',
  not_met: 'Not Met',
  uncertain: 'Uncertain',
  not_found: 'Not Found',
  contradictory: 'Contradictory',
};

// Display labels for review status
export const REVIEW_STATUS_LABELS: Record<ReviewStatus, string> = {
  pending: 'Pending',
  budget_pending: 'Budget Pending',
  in_progress: 'In Progress',
  pass_1_exclusions: 'Analyzing Exclusions',
  pass_2_inclusions: 'Analyzing Inclusions',
  pass_3_verification: 'Verification',
  completed: 'Completed',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

// Re-export ConfidenceLevel for convenience
export type { ConfidenceLevel };
